import React from 'react';
import { AlertTriangle, Clock } from 'lucide-react';
import { getStageMeta } from '../dashboardConstants';
import { useNavigate } from 'react-router-dom';

// ─── Avatar ──────────────────────────────────────────────────────────────────
export function Avatar({ name, size = 24 }) {
    const initials = (name || '').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
    const colors = [
        { bg: '#FFF1EC', text: '#D63F15' },
        { bg: '#F0FDF4', text: '#15803D' },
        { bg: '#FFFBEB', text: '#B45309' },
        { bg: '#FEF2F2', text: '#B91C1C' },
        { bg: '#F5F3FF', text: '#6D28D9' },
        { bg: '#FDF2F8', text: '#BE185D' },
        { bg: '#ECFEFF', text: '#0E7490' }
    ];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = (name || '').charCodeAt(i) + ((hash << 5) - hash);
    }
    const { bg, text } = colors[Math.abs(hash) % colors.length];
    return (
        <div
            className="p-avatar-v2"
            style={{
                width: size, height: size, fontSize: size * 0.42,
                backgroundColor: bg, color: text, fontWeight: '600',
                border: `1.5px solid ${text}20`, display: 'flex',
                alignItems: 'center', justifyContent: 'center', borderRadius: '50%'
            }}
        >
            {initials || '?'}
        </div>
    );
}

// ─── StatCard ─────────────────────────────────────────────────────────────────
export function StatCard({ title, value, icon: Icon, iconColorClass }) {
    const isRiskIndex = title === 'RISK INDEX';
    const isPipeline = title === 'PIPELINE VALUE';
    let finalIconColorClass = iconColorClass;
    if (isRiskIndex && value === 'Low') finalIconColorClass = 'low-green';
    let valStyle = {};
    if (isRiskIndex) {
        if (value === 'Low') valStyle = { color: '#10B981' };
        else if (value === 'Medium') valStyle = { color: '#F59E0B' };
        else valStyle = { color: '#EF4444' };
    }
    let renderedValue = value;
    if (isPipeline) {
        if (value.startsWith('$')) {
            renderedValue = (
                <span style={{ display: 'flex', alignItems: 'baseline' }}>
                    <span className="stat-currency-unit">$</span>{value.slice(1)}
                </span>
            );
        } else if (value.startsWith('AED')) {
            renderedValue = (
                <span style={{ display: 'flex', alignItems: 'baseline' }}>
                    <span className="stat-currency-unit">AED</span>{value.slice(3).trim()}
                </span>
            );
        }
    }
    return (
        <div className="stat-card-v4">
            <div className="stat-card-header" style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '20px', justifyContent: 'space-between', marginBottom: '8px' }}>

                <span className="stat-title-v4">{title}</span>
                {Icon && (
                    <div className={`stat-icon-wrapper ${finalIconColorClass}`}>
                        <Icon size={16} strokeWidth={2.2} />
                    </div>
                )}
            </div>
            <div className="stat-value-v4" style={valStyle}>{renderedValue}</div>
        </div>
    );
}

const getSingleTitle = (title) => {
    if (!title) return 'N/A';
    let val = Array.isArray(title) ? title[0] : title;
    if (typeof val === 'string') {
        const parts = val.split(/[;,|\n]/);
        val = parts[0] || val;
    }
    return String(val).trim().toUpperCase();
};

// ─── ProposalCard ─────────────────────────────────────────────────────────────
export function ProposalCard({ proposal, index }) {
    const navigate = useNavigate();
    const stage = proposal.status || proposal.proposal_stage || 'Drafting';
    const meta = getStageMeta(stage);
    const rfpId = proposal.rfp_id || proposal.id;
    const isDeadlineNear = proposal.is_deadline_near || false;
    const teamMembers = proposal.team_members || (proposal.created_by ? [proposal.created_by] : ['Sindhu Shetty']);
    const displayCount = proposal.team_member_count || teamMembers.length || 1;

    return (
        <div className="proposal-card-v3" onClick={() => navigate(`/proposals/analysis-result/${rfpId}`, { state: { rfpId } })}>
            <div className="p-header-v3">
                <div className="p-info-v3">
                    <h4 className="p-title-v3" style={{ border: 'none', boxShadow: 'none', background: 'transparent', padding: 0 }}>
                        {getSingleTitle(proposal.proposal_title || proposal.proposal_name || proposal.extraction_data?.proposal_title || 'Untitled Proposal')}
                    </h4>
                    <span className="p-client-v3">{proposal.client_name || 'Unknown Client'}</span>
                </div>
                <div className="p-badge-v3" style={{ backgroundColor: meta.bg, color: meta.text }}>
                    {meta.displayName}
                </div>
            </div>
            <div className="p-body-v3">
                <div className="p-meta-v3" style={{ justifyContent: 'space-between', width: '100%', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                        <div className="p-date-v3">
                            <Clock size={14} />
                            <span>{proposal.deadline_display || proposal.proposal_deadline || 'Jun 30'}</span>
                        </div>
                        {isDeadlineNear && (
                            <div className="risk-pill-v3">
                                <AlertTriangle size={12} color="#DC2626" />
                                <span style={{ color: '#DC2626' }}>Deadline Near</span>
                            </div>
                        )}
                    </div>
                </div>
                <div className="p-footer-v3" style={{ justifyContent: 'flex-start' }}>
                    <div className="p-team-v3">
                        <div className="avatar-group">
                            {teamMembers.slice(0, 3).map((member, i) => (
                                <Avatar key={i} name={member} size={24} />
                            ))}
                        </div>
                        <span className="team-count">{displayCount} {displayCount === 1 ? 'member' : 'members'}</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ─── PageHeader ───────────────────────────────────────────────────────────────
export function PageHeader({ title, subtitle }) {
    return (
        <div className="page-header">
            <div>
                <h1>{title}</h1>
                <p className="subtitle">{subtitle}</p>
            </div>
        </div>
    );
}
